import { useEffect, useState } from "react";
import { Table, Button, Modal, Form, Input, message, Select, DatePicker, Tag } from "antd";
import {
    getAllUsers,
    addUser,
    updateUser,
    deleteUser,
} from "../services/userService";
import { getAllGenders } from "../services/genderService";
import { toast } from "react-toastify";
import dayjs from "dayjs";

const UserManagement = () => {
    const [users, setUsers] = useState([]);
    const [adminUsers, setAdminUsers] = useState([]); // Sadece admin kullanıcılar
    const [genders, setGenders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [deletingUser, setDeletingUser] = useState(null);
    const [form] = Form.useForm();

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const userList = await getAllUsers();
            setUsers(userList);
            // Sadece Admin tipindeki kullanıcıları filtrele
            const admins = userList.filter(u =>
                u.userTypeDto?.name?.toLowerCase() === 'admin' ||
                u.userTypeDto?.name?.toLowerCase() === 'administrator'
            );
            setAdminUsers(admins);
        } catch {
            message.error("Kullanıcılar alınamadı.");
        } finally {
            setLoading(false);
        }
    };

    const fetchGenders = async () => {
        try {
            const genderList = await getAllGenders();
            setGenders(genderList);
        } catch {
            message.error("Cinsiyetler alınamadı.");
        }
    };

    const handleAddOrUpdate = async (values) => {
        // birthDate'i string'e çevir
        if (values.birthDate && dayjs.isDayjs(values.birthDate)) {
            values.birthDate = values.birthDate.format("YYYY-MM-DD");
        }

        try {
            let response;
            if (editingUser) {
                response = await updateUser({ ...editingUser, ...values });
            } else {
                // Yeni admin ekleme - userType'ı Admin olarak ayarla
                response = await addUser({
                    ...values,
                    // Backend'de admin tipi için gerekli alanları ekle
                });
            }

            if (response?.Status) {
                toast.success(response?.Message || response?.MessageCode || "İşlem başarılı.");
                fetchUsers();
                setIsModalOpen(false);
                form.resetFields();
                setEditingUser(null);
            } else {
                toast.error(response?.Message || "İşlem başarısız.");
            }
        } catch (error) {
            const msg = error?.response?.data?.Message || "İşlem sırasında hata oluştu.";
            toast.error(msg);
        }
    };

    const handleDelete = async (id) => {
        try {
            const response = await deleteUser(id);
            if (response?.Status) {
                toast.success(response?.Message || response?.MessageCode || "Silme başarılı.");
                fetchUsers();
            } else {
                toast.error(response?.Message || "Silme başarısız.");
            }
        } catch (error) {
            const msg = error?.response?.data?.Message || "Silme sırasında hata oluştu.";
            toast.error(msg);
        }
    };

    useEffect(() => {
        fetchUsers();
        fetchGenders();
    }, []);

    // Admin kullanıcılar için kolonlar (tam bilgi)
    const adminColumns = [
        { title: "Ad", dataIndex: "name" },
        {
            title: "Kullanıcı Tipi",
            dataIndex: "userTypeDto",
            render: (userTypeDto) => (
                <Tag color="red">{userTypeDto?.name || "-"}</Tag>
            ),
        },
        {
            title: "Durum",
            dataIndex: "status",
            render: (status) => (
                <Tag color={status ? "green" : "red"}>
                    {status ? "Aktif" : "Pasif"}
                </Tag>
            ),
        },
        {
            title: "İşlem",
            render: (_, record) => (
                <>
                    <Button
                        onClick={() => {
                            setEditingUser(record);
                            form.setFieldsValue({
                                ...record,
                                genderId: record.genderId,
                                birthDate: record.birthDate ? dayjs(record.birthDate) : null,
                            });
                            setIsModalOpen(true);
                        }}
                        style={{ marginRight: 8 }}
                    >
                        Güncelle
                    </Button>
                    <Button
                        danger
                        onClick={() => {
                            setDeletingUser(record);
                            setIsDeleteModalOpen(true);
                        }}
                    >
                        Sil
                    </Button>
                </>
            ),
        },
    ];

    // Normal kullanıcılar için kolonlar (sadece ad ve tip)
    const userColumns = [
        { title: "Ad", dataIndex: "name" },
        {
            title: "Kullanıcı Tipi",
            dataIndex: "userTypeDto",
            render: (userTypeDto) => (
                <Tag color="blue">{userTypeDto?.name || "-"}</Tag>
            ),
        },
        {
            title: "Durum",
            dataIndex: "userStatusDto",
            render: (userStatusDto) => (
                <Tag color={userStatusDto?.name === "Approved" ? "green" : "orange"}>
                    {userStatusDto?.description || userStatusDto?.name || "-"}
                </Tag>
            ),
        },
        {
            title: "Kayıt Tarihi",
            dataIndex: "createTime",
            render: (date) => date ? dayjs(date).format("DD.MM.YYYY") : "-",
        },
    ];

    // Normal kullanıcıları filtrele (admin olmayanlar)
    const normalUsers = users.filter(u =>
        u.userTypeDto?.name?.toLowerCase() !== 'admin' &&
        u.userTypeDto?.name?.toLowerCase() !== 'administrator'
    );

    return (
        <div>
            <h2>Admin Yönetimi</h2>
            <p style={{ color: '#666', marginBottom: 16 }}>
                Bu sayfadan sadece admin kullanıcıları yönetebilirsiniz.
            </p>
            <Button type="primary" onClick={() => setIsModalOpen(true)}>
                Yeni Admin Ekle
            </Button>

            <Table
                rowKey="id"
                loading={loading}
                dataSource={adminUsers}
                columns={adminColumns}
                style={{ marginTop: 16 }}
                locale={{ emptyText: "Henüz admin kullanıcı bulunmuyor." }}
            />

            <h2 style={{ marginTop: 40 }}>Kayıtlı Kullanıcılar</h2>
            <p style={{ color: '#666', marginBottom: 16 }}>
                Sistemde kayıtlı müşteri kullanıcılarının listesi. Gizlilik nedeniyle sadece temel bilgiler gösterilmektedir.
            </p>

            <Table
                rowKey="id"
                loading={loading}
                dataSource={normalUsers}
                columns={userColumns}
                locale={{ emptyText: "Henüz kayıtlı kullanıcı bulunmuyor." }}
            />

            {/* Ekle/Güncelle Modal - Sadece Admin için */}
            <Modal
                title={editingUser ? "Admin Güncelle" : "Yeni Admin Ekle"}
                open={isModalOpen}
                onCancel={() => {
                    setIsModalOpen(false);
                    setEditingUser(null);
                    form.resetFields();
                }}
                onOk={() => form.submit()}
                destroyOnClose
            >
                <Form form={form} onFinish={handleAddOrUpdate} layout="vertical">
                    <Form.Item name="name" label="Ad Soyad" rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>

                    <Form.Item name="phone" label="Telefon" rules={[{ required: true }]}>
                        <Input placeholder="5554443322" />
                    </Form.Item>

                    <Form.Item
                        name="email"
                        label="E-posta"
                        rules={[{ required: true, type: "email" }]}
                    >
                        <Input />
                    </Form.Item>

                    {!editingUser && (
                        <Form.Item
                            name="password"
                            label="Şifre"
                            rules={[{ required: true, message: "Şifre zorunlu" }]}
                        >
                            <Input.Password />
                        </Form.Item>
                    )}

                    <Form.Item
                        name="genderId"
                        label="Cinsiyet"
                        rules={[{ required: true, message: "Cinsiyet seçimi zorunlu" }]}
                    >
                        <Select
                            placeholder="Seçiniz"
                            options={genders.map((g) => ({
                                label: g.name,
                                value: g.id,
                            }))}
                        />
                    </Form.Item>

                    <Form.Item
                        name="birthDate"
                        label="Doğum Tarihi"
                        rules={[{ required: true, message: "Doğum tarihi zorunlu" }]}
                    >
                        <DatePicker format="DD.MM.YYYY" style={{ width: "100%" }} />
                    </Form.Item>
                </Form>
            </Modal>

            {/* Silme Modalı */}
            <Modal
                title="Silme işlemini onaylıyor musunuz?"
                open={isDeleteModalOpen}
                onCancel={() => setIsDeleteModalOpen(false)}
                onOk={() => {
                    if (deletingUser) {
                        handleDelete(deletingUser.id);
                        setIsDeleteModalOpen(false);
                    }
                }}
                okText="Evet, Sil"
                okType="danger"
                cancelText="Vazgeç"
            >
                <p>
                    "<strong>{deletingUser?.name}</strong>" adlı admin silinecek. Onaylıyor musunuz?
                </p>
            </Modal>
        </div>
    );
};

export default UserManagement;
